import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { arrayMove } from '@dnd-kit/sortable';

import { FormField, FIELD_SCHEMA_VERSION, FieldSchema } from '~forms/types';

interface FormBuilderState {
  fields: FormField[];
  selectedFieldId: string | null;
  isDirty: boolean;
}

const initialState: FormBuilderState = {
  fields: [],
  selectedFieldId: null,
  isDirty: false,
};

const formBuilderSlice = createSlice({
  name: 'forms/formBuilder',
  initialState,
  reducers: {
    setFields: (state, action: PayloadAction<FormField[]>) => {
      state.fields = action.payload;
      state.isDirty = false;
    },

    addField: (state, action: PayloadAction<FormField>) => {
      state.fields = [...state.fields, action.payload];
      state.selectedFieldId = action.payload.id;
      state.isDirty = true;
    },

    updateField: (state, action: PayloadAction<Partial<FormField> & { id: string }>) => {
      const index = state.fields.findIndex((f) => f.id === action.payload.id);
      if (index !== -1) {
        state.fields[index] = { ...state.fields[index], ...action.payload } as FormField;
        state.isDirty = true;
      }
    },

    duplicateField: (state, action: PayloadAction<string>) => {
      const index = state.fields.findIndex((f) => f.id === action.payload);
      if (index !== -1) {
        const original = state.fields[index];
        const duplicate: FormField = {
          ...original,
          id: crypto.randomUUID(),
        };
        const newFields = [...state.fields];
        newFields.splice(index + 1, 0, duplicate);
        state.fields = newFields;
        state.selectedFieldId = duplicate.id;
        state.isDirty = true;
      }
    },

    removeField: (state, action: PayloadAction<string>) => {
      state.fields = state.fields.filter((f) => f.id !== action.payload);
      if (state.selectedFieldId === action.payload) {
        state.selectedFieldId = null;
      }
      state.isDirty = true;
    },

    reorderFields: (state, action: PayloadAction<{ activeId: string; overId: string }>) => {
      const { activeId, overId } = action.payload;
      const oldIndex = state.fields.findIndex((f) => f.id === activeId);
      const newIndex = state.fields.findIndex((f) => f.id === overId);
      if (oldIndex !== -1 && newIndex !== -1) {
        state.fields = arrayMove(state.fields, oldIndex, newIndex);
        state.isDirty = true;
      }
    },

    selectField: (state, action: PayloadAction<string | null>) => {
      state.selectedFieldId = action.payload;
    },

    // Inserts a merge token as an inline {{merge:<token>}} sentinel into a field property.
    // Defaults to `default_value` (prefill) — merge fields populate part of a field's value, not a
    // standalone field.
    insertInlineToken: (
      state,
      action: PayloadAction<{ fieldId: string; fieldProp?: 'label' | 'help_text' | 'default_value'; token: string }>,
    ) => {
      const { fieldId, token } = action.payload;
      const fieldProp = action.payload.fieldProp ?? 'default_value';
      const index = state.fields.findIndex((f) => f.id === fieldId);
      if (index !== -1) {
        const field = state.fields[index];
        const sentinel = `{{merge:${token}}}`;
        const current = (field[fieldProp] as string | undefined) ?? '';
        state.fields[index] = { ...field, [fieldProp]: current + sentinel } as FormField;
        state.isDirty = true;
      }
    },

    markClean: (state) => {
      state.isDirty = false;
    },

    resetBuilder: () => initialState,
  },
});

export const {
  setFields,
  addField,
  updateField,
  duplicateField,
  removeField,
  reorderFields,
  selectField,
  insertInlineToken,
  markClean,
  resetBuilder,
} = formBuilderSlice.actions;

export default formBuilderSlice.reducer;

// Helper to build a FieldSchema from current fields
export const buildFieldSchema = (fields: FormField[]): FieldSchema => ({
  version: FIELD_SCHEMA_VERSION,
  fields,
});
